export default function SchemaMarkup() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Cabins in Highlands NC',
    description:
      'Explore a curated selection of cabins in Highlands, North Carolina, including luxury cabins, pet-friendly stays, and cabins near waterfalls.',
    url: `${siteUrl}/best-luxury-cabins`,
    inLanguage: 'en-US',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${siteUrl}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cabins in Highlands NC',
          item: `${siteUrl}/best-luxury-cabins`,
        },
      ],
    },
    relatedLink: [
      `${siteUrl}/things-to-do`,
      `${siteUrl}/`,
    ],
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Cabins in Highlands NC',
    description:
      'A curated selection of cabins in Highlands, North Carolina — including luxury cabins, pet-friendly stays, and cabins near waterfalls.',
    numberOfItems: 5,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Glen Falls Luxury Cabin',
        description:
          "Glen Falls Luxury Cabin offers a unique blend of privacy, comfort, and location — just minutes from one of Highlands' most beautiful waterfalls. Thoughtfully designed and surrounded by nature, it provides a peaceful retreat while still being close to town.",
        url: 'https://www.airbnb.com/rooms/900012473285450950',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Modern Mountain Stream Overlook',
        description:
          'A beautifully designed cabin in Highlands offering a peaceful mountain setting with upscale finishes. Ideal for a relaxing getaway while still being just a short drive from town and nearby trails.',
        url: 'https://www.airbnb.com/rooms/1309751339447568824',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Lark Lodge',
        description:
          "A stylish and inviting retreat in Highlands featuring comfortable interiors and a serene natural setting. Perfect for guests looking to unwind while staying close to the area's shops and restaurants.",
        url: 'https://www.airbnb.com/rooms/1617475116852690845',
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'Acorn Cabin — Close to Town',
        description:
          'A charming mountain cabin surrounded by nature, offering a cozy and comfortable stay in Highlands. A great choice for a quiet escape with convenient access to local attractions.',
        url: 'https://www.airbnb.com/rooms/50702814',
      },
      {
        '@type': 'ListItem',
        position: 5,
        name: 'Stag Ridge — Luxury Highlands Escape',
        description:
          'A thoughtfully designed cottage blending comfort and simplicity, set in a tranquil Highlands setting. Ideal for enjoying the natural beauty of the area while remaining close to town.',
        url: 'https://www.airbnb.com/rooms/1180227781183226897',
      },
    ],
  };

  const lodgingSchema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: 'Glen Falls Luxury Cabin',
    description:
      "Glen Falls Luxury Cabin offers a unique blend of privacy, comfort, and location — just minutes from one of Highlands' most beautiful waterfalls. Thoughtfully designed and surrounded by nature, it provides a peaceful retreat while still being close to town. With a consistent 5.0-star rating and recognition as an Airbnb Guest Favorite, it stands among the top homes in the area for guests seeking a refined and relaxing Highlands experience.",
    url: `${siteUrl}`,
    sameAs: [
      'https://www.airbnb.com/rooms/900012473285450950',
      'https://www.vrbo.com/3557955',
    ],
    image: 'https://static.readdy.ai/image/ad5520ee7149a263b01f4e3410f2d685/ba0d51886e955561a1eff3aa82f68703.jpeg',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Highlands',
      addressRegion: 'NC',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.0499,
      longitude: -83.1985,
    },
    starRating: {
      '@type': 'Rating',
      ratingValue: '5.0',
      bestRating: '5',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      reviewCount: '48',
      bestRating: '5',
    },
    amenityFeature: [
      { '@type': 'LocationFeatureSpecification', name: 'Pet Friendly', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Outdoor Wood-Burning Fireplace', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Near Glen Falls', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Peaceful Wooded Setting', value: true },
      { '@type': 'LocationFeatureSpecification', name: 'Close to Downtown Highlands', value: true },
    ],
    petsAllowed: true,
    priceRange: '$$$',
    checkinTime: '15:00',
    checkoutTime: '11:00',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What makes Highlands, NC cabins different from other mountain rentals?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Highlands occupies an ecological sweet spot — over 4,000 feet in elevation with rainfall rivaling the Pacific Northwest. The result is an extraordinarily lush landscape unlike anywhere else in the East. Properties here offer cool summers, dramatic foliage, cascading waterfalls, and a level of natural immersion that's genuinely rare. Combined with a world-class culinary and arts scene, Highlands cabins offer a complete luxury experience.",
        },
      },
      {
        '@type': 'Question',
        name: 'When should I book a Highlands cabin?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Book as early as possible — ideally 6–12 months in advance for summer weekends and peak fall color (mid-October). The shoulder seasons (late April–May and November) offer availability with fewer crowds. Winter bookings can often be made 4–8 weeks out and offer significant value.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are there truly pet-friendly luxury cabins in Highlands NC?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — many Highlands cabins welcome pets and provide a great experience for guests traveling with dogs. Look for properties with nearby walking trails, wooded surroundings, and outdoor spaces to enjoy together.',
        },
      },
      {
        '@type': 'Question',
        name: 'Which cabins are closest to the waterfalls?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Highlands is known for its easy access to some of the most beautiful waterfalls in North Carolina, including Bridal Veil Falls, Dry Falls, and Glen Falls. Many cabins are located within a short 5–15 minute drive of these sites.',
        },
      },
      {
        '@type': 'Question',
        name: 'What amenities can I expect in a luxury Highlands cabin?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Highlands cabins are known for combining comfort with a strong connection to nature. Common features include gourmet kitchens, indoor stone fireplaces, high-speed WiFi, and spacious living areas. Many properties also offer covered outdoor spaces, such as decks and wood-burning fireplaces.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Highlands accessible year-round?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, though US-64 can see winter weather events. Most Highlands properties are accessible year-round with standard vehicles. For properties on steeper mountain roads, 4WD or AWD is recommended in winter.',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(lodgingSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}
